import { useCallback, useMemo } from "react";
import { useQueryClient, replaceEqualDeep } from "@tanstack/react-query";

import { useUpdateVideo, videoKey } from "@app/hooks/videos";

import { queueImageVariationsWrite } from "./shared";

/**
 * Merging writer for a project's `imageVariations`.
 *
 * Returns `{ persist, isPending }`: `persist(newVariations)` merges them into
 * the video's `imageVariations` and persists the result, and `isPending` is
 * true while this writer has a write in flight. Writes are queued per project
 * (see `queueImageVariationsWrite`), so the snapshot merged into is always the
 * previous write's outcome.
 *
 * Pass the same writer to every hook that persists variations of a project —
 * they then share one mutation observer, and `isPending` covers all of them.
 */
export const usePersistImageVariations = (projectId) => {
  const client = useQueryClient();
  const updateVideo = useUpdateVideo(projectId);

  // `mutateAsync` is bound once per mutation observer, so it keeps the same
  // identity for the life of the hook — unlike the mutation object around it.
  const { mutateAsync: updateVideoAsync } = updateVideo;

  const persist = useCallback(
    (newVariations) => {
      if (!newVariations?.length) {
        return Promise.resolve();
      }

      return queueImageVariationsWrite(projectId, async () => {
        const videoData = client.getQueryData(videoKey.detail(projectId));

        const allVariations = new Map(
          videoData?.imageVariations?.map((v) => [v.key, v]) ?? [],
        );

        newVariations.forEach((variation) => {
          allVariations.set(variation.key, variation);
        });

        const imageVariations = replaceEqualDeep(
          videoData?.imageVariations,
          Array.from(allVariations.values()),
        );

        // Skip the update when the variations did not actually change
        if (imageVariations === videoData?.imageVariations) {
          return;
        }

        await updateVideoAsync({ imageVariations });
      });
    },
    [client, projectId, updateVideoAsync],
  );

  return useMemo(
    () => ({ persist, isPending: updateVideo.isPending }),
    [persist, updateVideo.isPending],
  );
};
